import {
  AUTO_FILL_PLAYER_ID_PREFIX,
  AUTO_FILL_PLAYER_RATING,
  calculatePlayerOverall,
  deriveReturnAbility,
  FRANCHISE_JERSEY_NUMBER_RANGES,
  FRANCHISE_PLAYER_POSITIONS,
  isAutoFillPlayer,
  PLAYER_STAT_KEYS,
  type FranchisePlayer,
  type FranchisePlayerPosition,
  type PlayerRatings,
} from './player-model';
import { defaultPlayerPlayStyle, isPlayerPlayStyleForPosition, type PlayerPlayStyle } from './player-play-style';

export const FRANCHISE_MINIMUM_POSITION_COUNTS: Record<FranchisePlayerPosition, number> = {
  QB: 1,
  RB: 3,
  FB: 0,
  WR: 4,
  TE: 2,
  OT: 2,
  OG: 2,
  C: 1,
  DE: 3,
  DT: 3,
  LB: 4,
  CB: 3,
  S: 3,
  K: 1,
  P: 0,
};

export const FRANCHISE_MINIMUM_ROSTER_SIZE = Object.values(FRANCHISE_MINIMUM_POSITION_COUNTS)
  .reduce((total, count) => total + count, 0);

/**
 * Slot template for the "Apply Recommended" roster builder. Mirrors the
 * gameplay minimums so a fully-applied roster fields no auto-fill
 * placeholders; spread per-position overrides on top to recommend extra
 * depth beyond a minimum. The builder derives everything from this template.
 */
export const RECOMMENDED_ROSTER_POSITION_COUNTS: Record<FranchisePlayerPosition, number> = {
  ...FRANCHISE_MINIMUM_POSITION_COUNTS,
};

export const RECOMMENDED_ROSTER_SIZE = Object.values(RECOMMENDED_ROSTER_POSITION_COUNTS)
  .reduce((total, count) => total + count, 0);

export type FranchiseDepthChartRoles = {
  kickReturner?: string;
  puntReturner?: string;
  returner?: string;
};

export type FranchiseDepthChartConfig = {
  positions: Record<FranchisePlayerPosition, string[]>;
  roles: FranchiseDepthChartRoles;
  playerStyles?: Record<string, PlayerPlayStyle>;
};

export type FranchiseDepthChartInput = {
  positions?: Partial<Record<FranchisePlayerPosition, string[]>>;
  roles?: FranchiseDepthChartRoles;
  playerStyles?: Record<string, PlayerPlayStyle>;
};

export type FranchiseDepthChartEntry = {
  franchiseId: string;
  depthChart: FranchiseDepthChartConfig;
  hasOverride: boolean;
  updatedAt: string | null;
};

export type FranchiseDepthChart = {
  qb: FranchisePlayer | undefined;
  rb: FranchisePlayer | undefined;
  fb: FranchisePlayer | undefined;
  wrs: FranchisePlayer[];
  te: FranchisePlayer | undefined;
  ol: FranchisePlayer[];
  de: FranchisePlayer[];
  dt: FranchisePlayer[];
  lb: FranchisePlayer[];
  cb: FranchisePlayer[];
  s: FranchisePlayer[];
  k: FranchisePlayer | undefined;
  p: FranchisePlayer | undefined;
  kickReturner: FranchisePlayer | undefined;
  puntReturner: FranchisePlayer | undefined;
  returner: FranchisePlayer | undefined;
};

export type FranchiseDepthChartMinimumIssue = {
  position: FranchisePlayerPosition;
  actual: number;
  minimum: number;
};

export const FRANCHISE_RETURNER_CANDIDATE_LIMITS = {
  WR: 4,
  RB: 3,
  CB: 3,
  S: 2,
  TE: 1,
  FB: 1,
  LB: 2,
} as const satisfies Partial<Record<FranchisePlayerPosition, number>>;

function uniqueIds(ids: readonly string[] | undefined): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  (ids || []).forEach((id) => {
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    result.push(id);
  });

  return result;
}

function comparePlayers(left: FranchisePlayer, right: FranchisePlayer): number {
  // Real players always rank ahead of auto-fill placeholders, even at a lower
  // overall — generated depth only covers slots no real player can take.
  return Number(isAutoFillPlayer(left)) - Number(isAutoFillPlayer(right))
    || right.overall - left.overall
    || left.number - right.number;
}

function playerIdsForPosition(players: FranchisePlayer[], position: FranchisePlayerPosition | string): string[] {
  return players
    .filter((player) => player.position === position)
    .sort(comparePlayers)
    .map((player) => player.id);
}

function playerById(players: FranchisePlayer[]): Map<string, FranchisePlayer> {
  return new Map(players.map((player) => [player.id, player]));
}

function resolvePositionOrder(
  players: FranchisePlayer[],
  position: FranchisePlayerPosition,
  override?: readonly string[],
): string[] {
  const allowedIds = playerIdsForPosition(players, position);
  const allowedSet = new Set(allowedIds);
  const requested = uniqueIds(override).filter((id) => allowedSet.has(id));
  const requestedSet = new Set(requested);
  const remaining = allowedIds.filter((id) => !requestedSet.has(id));
  return [...requested, ...remaining];
}

export function cloneFranchiseDepthChartConfig(config: FranchiseDepthChartConfig): FranchiseDepthChartConfig {
  return {
    positions: FRANCHISE_PLAYER_POSITIONS.reduce((result, position) => {
      result[position] = [...(config.positions[position] || [])];
      return result;
    }, {} as Record<FranchisePlayerPosition, string[]>),
    roles: {
      ...config.roles,
    },
    playerStyles: { ...(config.playerStyles || {}) },
  };
}

export function getFranchiseDepthChartMinimumIssues(
  config: FranchiseDepthChartConfig | null | undefined,
): FranchiseDepthChartMinimumIssue[] {
  return FRANCHISE_PLAYER_POSITIONS
    .map((position) => ({
      position,
      actual: config?.positions?.[position]?.length || 0,
      minimum: FRANCHISE_MINIMUM_POSITION_COUNTS[position],
    }))
    .filter((issue) => issue.actual < issue.minimum);
}

function nextAutoFillJerseyNumber(position: FranchisePlayerPosition, usedNumbers: Set<number>): number {
  const [min, max] = FRANCHISE_JERSEY_NUMBER_RANGES[position];
  for (let number = min; number <= max; number += 1) {
    if (!usedNumbers.has(number)) {
      usedNumbers.add(number);
      return number;
    }
  }

  let fallback = max + 1;
  while (usedNumbers.has(fallback)) {
    fallback += 1;
  }
  usedNumbers.add(fallback);
  return fallback;
}

function buildAutoFillPlayer(
  position: FranchisePlayerPosition,
  teamLabel: string,
  depthSlot: number,
  usedIds: Set<string>,
  usedNumbers: Set<number>,
): FranchisePlayer {
  let slot = depthSlot;
  while (usedIds.has(`${AUTO_FILL_PLAYER_ID_PREFIX}${teamLabel}-${position}-${slot}`)) {
    slot += 1;
  }
  const id = `${AUTO_FILL_PLAYER_ID_PREFIX}${teamLabel}-${position}-${slot}`;
  usedIds.add(id);

  const ratings = PLAYER_STAT_KEYS.reduce((result, key) => {
    result[key] = AUTO_FILL_PLAYER_RATING;
    return result;
  }, {} as Partial<Record<typeof PLAYER_STAT_KEYS[number], number>>) as PlayerRatings;

  return {
    id,
    name: `${teamLabel}-${position}-${slot}`,
    position,
    years: 1,
    number: nextAutoFillJerseyNumber(position, usedNumbers),
    overall: calculatePlayerOverall(position, ratings),
    ratings,
  };
}

/**
 * Pad a roster so every position group reaches its franchise minimum by
 * appending deterministic auto-fill placeholders ({TEAM}-{POS}-{depth},
 * AUTO_FILL_PLAYER_RATING in every stat). Real players are never displaced —
 * placeholders only cover vacancies — and a roster already at the minimums is
 * returned unchanged. Fully derived from the input (no randomness), so it is
 * safe inside the deterministic sim.
 */
export function autoFillRosterToMinimums(
  roster: FranchisePlayer[],
  teamLabel: string,
  positionMinimums: Record<FranchisePlayerPosition, number> = FRANCHISE_MINIMUM_POSITION_COUNTS,
): FranchisePlayer[] {
  const label = String(teamLabel || 'TEAM').toUpperCase();
  const usedIds = new Set(roster.map((player) => player.id));
  const usedNumbers = new Set(roster.map((player) => player.number));
  const fillers: FranchisePlayer[] = [];

  FRANCHISE_PLAYER_POSITIONS.forEach((position) => {
    const rostered = roster.filter((player) => player.position === position).length;
    const minimum = Math.max(0, Math.floor(positionMinimums[position] || 0));
    for (let depth = rostered; depth < minimum; depth += 1) {
      fillers.push(buildAutoFillPlayer(position, label, depth + 1, usedIds, usedNumbers));
    }
  });

  return fillers.length ? [...roster, ...fillers] : roster;
}

export function getDefaultFranchiseDepthChartConfig(players: FranchisePlayer[]): FranchiseDepthChartConfig {
  return {
    positions: FRANCHISE_PLAYER_POSITIONS.reduce((result, position) => {
      result[position] = playerIdsForPosition(players, position);
      return result;
    }, {} as Record<FranchisePlayerPosition, string[]>),
    roles: {},
    playerStyles: Object.fromEntries(players.map((player) => [
      player.id,
      isPlayerPlayStyleForPosition(player.playStyle, player.position)
        ? player.playStyle
        : defaultPlayerPlayStyle(player.id, player.position),
    ])),
  };
}

/**
 * Build a depth chart from an owner's available players without changing any
 * player's listed position. Each position room is ranked by its native overall
 * and filled to that room's requested count. Slots come from `positionCounts`
 * ({@link RECOMMENDED_ROSTER_POSITION_COUNTS} by default, 32 players); with a
 * short pool the chart simply comes up short and the sim's auto-fill covers the
 * gaps. Depth order within a position is best-overall first. Returner
 * roles are reset to auto. Deterministic for a given input.
 */
export function buildRecommendedDepthChartConfig(
  players: FranchisePlayer[],
  positionCounts: Record<FranchisePlayerPosition, number> = RECOMMENDED_ROSTER_POSITION_COUNTS,
): FranchiseDepthChartConfig {
  const candidates = players.filter((player) => !isAutoFillPlayer(player));

  const filled = FRANCHISE_PLAYER_POSITIONS.reduce((result, position) => {
    result[position] = candidates
      .filter((player) => player.position === position)
      .sort((left, right) => right.overall - left.overall || left.id.localeCompare(right.id))
      .slice(0, Math.max(0, positionCounts[position] || 0))
      .map((player) => player.id);
    return result;
  }, {} as Record<FranchisePlayerPosition, string[]>);

  return {
    positions: filled,
    roles: {},
    playerStyles: Object.fromEntries(FRANCHISE_PLAYER_POSITIONS.flatMap((position) =>
      filled[position].map((playerId) => {
        const player = candidates.find((entry) => entry.id === playerId);
        return [
          playerId,
          isPlayerPlayStyleForPosition(player?.playStyle, position)
            ? player.playStyle
            : defaultPlayerPlayStyle(playerId, position),
        ];
      }),
    )),
  };
}

export function mergeFranchiseDepthChart(
  players: FranchisePlayer[],
  input?: FranchiseDepthChartInput | FranchiseDepthChartConfig | null,
): FranchiseDepthChartConfig {
  const defaults = getDefaultFranchiseDepthChartConfig(players);
  const knownPlayers = playerById(players);
  const positions = FRANCHISE_PLAYER_POSITIONS.reduce((result, position) => {
    result[position] = resolvePositionOrder(players, position, input?.positions?.[position]);
    return result;
  }, {} as Record<FranchisePlayerPosition, string[]>);

  const legacyReturner = typeof input?.roles?.returner === 'string' && knownPlayers.has(input.roles.returner)
    ? input.roles.returner
    : undefined;
  const kickReturner = typeof input?.roles?.kickReturner === 'string' && knownPlayers.has(input.roles.kickReturner)
    ? input.roles.kickReturner
    : legacyReturner;
  const puntReturner = typeof input?.roles?.puntReturner === 'string' && knownPlayers.has(input.roles.puntReturner)
    ? input.roles.puntReturner
    : legacyReturner;
  const assignedPosition = new Map<string, FranchisePlayerPosition>();
  FRANCHISE_PLAYER_POSITIONS.forEach((position) => positions[position].forEach((id) => assignedPosition.set(id, position)));
  const playerStyles = Object.fromEntries([...assignedPosition].map(([playerId, position]) => {
    const requested = input?.playerStyles?.[playerId];
    return [playerId, isPlayerPlayStyleForPosition(requested, position)
      ? requested
      : defaultPlayerPlayStyle(playerId, position)];
  }));

  return {
    positions: {
      ...defaults.positions,
      ...positions,
    },
    roles: {
      ...defaults.roles,
      ...(kickReturner ? { kickReturner } : {}),
      ...(puntReturner ? { puntReturner } : {}),
    },
    playerStyles,
  };
}

function orderedPlayers(
  players: FranchisePlayer[],
  position: FranchisePlayerPosition | string,
  depthChart?: FranchiseDepthChartConfig | null,
): FranchisePlayer[] {
  const orderedIds = depthChart?.positions?.[position as FranchisePlayerPosition];
  const matchingPlayers = players.filter((player) => player.position === position);
  if (!orderedIds?.length) {
    return matchingPlayers;
  }

  const byId = new Map(matchingPlayers.map((player) => [player.id, player]));
  const resolved = orderedIds
    .map((id) => byId.get(id))
    .filter((player): player is FranchisePlayer => Boolean(player));
  const resolvedIds = new Set(resolved.map((player) => player.id));
  const remaining = matchingPlayers.filter((player) => !resolvedIds.has(player.id));
  return [...resolved, ...remaining];
}

export function resolveDepthChartPlayer(
  players: FranchisePlayer[],
  playerId: string | null | undefined,
): FranchisePlayer | undefined {
  if (!playerId) {
    return undefined;
  }
  return players.find((player) => player.id === playerId);
}

export function playersAtPosition(
  players: FranchisePlayer[],
  position: FranchisePlayerPosition | string,
  depthChart?: FranchiseDepthChartConfig | null,
): FranchisePlayer[] {
  return orderedPlayers(players, position, depthChart);
}

export function orderFranchiseRoster(
  players: FranchisePlayer[],
  depthChart?: FranchiseDepthChartConfig | null,
): FranchisePlayer[] {
  return FRANCHISE_PLAYER_POSITIONS.flatMap((position) => playersAtPosition(players, position, depthChart));
}

function firstPlayers(players: FranchisePlayer[], count: number): FranchisePlayer[] {
  return players.slice(0, count);
}

function compareReturners(left: FranchisePlayer, right: FranchisePlayer): number {
  return Number(isAutoFillPlayer(left)) - Number(isAutoFillPlayer(right))
    || deriveReturnAbility(right) - deriveReturnAbility(left)
    || right.overall - left.overall
    || left.number - right.number;
}

export function getFranchiseReturnerCandidates(
  players: FranchisePlayer[],
  depthChart?: FranchiseDepthChartConfig | null,
  limit: number = 6,
  includePlayerIds?: readonly (string | null | undefined)[],
): FranchisePlayer[] {
  const seen = new Set<string>();
  const candidates = Object.entries(FRANCHISE_RETURNER_CANDIDATE_LIMITS)
    .flatMap(([position, count]) => playersAtPosition(players, position, depthChart).slice(0, count))
    .filter((player) => {
      if (seen.has(player.id)) {
        return false;
      }
      seen.add(player.id);
      return true;
    })
    .sort(compareReturners);

  const includedPlayers = (includePlayerIds || [])
    .map((playerId) => resolveDepthChartPlayer(players, playerId))
    .filter((player): player is FranchisePlayer => Boolean(player))
    .filter((player, index, list) => list.findIndex((entry) => entry.id === player.id) === index);

  includedPlayers
    .slice()
    .reverse()
    .forEach((player) => {
      if (!candidates.some((entry) => entry.id === player.id)) {
        candidates.unshift(player);
      }
    });

  return candidates.slice(0, Math.max(limit, includedPlayers.length));
}

function defaultReturner(
  players: FranchisePlayer[],
  depthChart?: FranchiseDepthChartConfig | null,
): FranchisePlayer | undefined {
  return getFranchiseReturnerCandidates(players, depthChart, 1)[0];
}

export function buildFranchiseDepthChart(
  players: FranchisePlayer[],
  depthChart?: FranchiseDepthChartConfig | null,
): FranchiseDepthChart {
  const qb = playersAtPosition(players, 'QB', depthChart)[0];
  const rb = playersAtPosition(players, 'RB', depthChart)[0];
  const fb = playersAtPosition(players, 'FB', depthChart)[0];
  const wrs = firstPlayers(playersAtPosition(players, 'WR', depthChart), 3);
  const te = playersAtPosition(players, 'TE', depthChart)[0];
  const ol = [
    ...firstPlayers(playersAtPosition(players, 'OT', depthChart), 2),
    ...firstPlayers(playersAtPosition(players, 'OG', depthChart), 2),
    playersAtPosition(players, 'C', depthChart)[0],
  ].filter((player): player is FranchisePlayer => Boolean(player));
  const de = firstPlayers(playersAtPosition(players, 'DE', depthChart), 2);
  const dt = firstPlayers(playersAtPosition(players, 'DT', depthChart), 2);
  const lb = firstPlayers(playersAtPosition(players, 'LB', depthChart), 3);
  const cb = firstPlayers(playersAtPosition(players, 'CB', depthChart), 2);
  const s = firstPlayers(playersAtPosition(players, 'S', depthChart), 2);
  const k = playersAtPosition(players, 'K', depthChart)[0];
  const p = playersAtPosition(players, 'P', depthChart)[0] || k;
  const defaultSpecialist = defaultReturner(players, depthChart);
  const kickReturner = resolveDepthChartPlayer(players, depthChart?.roles?.kickReturner) || defaultSpecialist;
  const puntReturner = resolveDepthChartPlayer(players, depthChart?.roles?.puntReturner) || defaultSpecialist;
  const returner = kickReturner || puntReturner;

  return { qb, rb, fb, wrs, te, ol, de, dt, lb, cb, s, k, p, kickReturner, puntReturner, returner };
}
